/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// Component imports
import { Alert, Button, Checkbox, Divider, Form, Input, Skeleton, Space, Tag, Typography } from 'antd';
import LabelWithOriginalText from '../../../Forms/Fields/LabelWithOriginalText';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { useFetchBotQuery } from '../../../../store/bots/botsApi';
import { NAV_BOT } from '../../../../constants/navigation';
import replaceUrlParams from '../../../../utils/string/replaceUrlParams';

const { Text } = Typography;

const AuthenticateUserNodeForm = ({ form }) => {
  const { t } = useTranslation();
  const { orgId, tenantId } = useParams();
  const navigate = useNavigate();
  const { isEditingPrimaryLanguage, botLanguages, currentBotLanguage, primaryBotLanguage } = useBotLanguage();
  const { data: bot, isFetching: isFetchingBot } = useFetchBotQuery({ orgId, tenantId }, { skip: !tenantId });
  const checkOnly = Form.useWatch(['data', 'requireOidcSignIn'], form) === false;

  if (isFetchingBot) {
    return <Skeleton active />;
  }

  const hasOidcConfig = !!bot?.settings?.security?.oidc?.integration;
  const preAuthProviders = bot?.settings?.security?.preAuthTokenProviders ?? [];

  return (
    <Fragment>
      {!hasOidcConfig && (
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message={
            <Text>
              {t('No OIDC authentication configured.')}
              {' '}
              <Button
                type="link"
                className="px-1"
                onClick={() => {
                  navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?view=security`);
                }}
              >
                {t('Configure in Security settings')}
              </Button>
            </Text>
          }
        />
      )}

      <Space direction="vertical" size={8} className="mb-4 fs-sm">
        <Text>
          {t('Verifies whether the end-user is authenticated before continuing the flow.')}
        </Text>
        <Text strong>{t('How it works:')}</Text>
        <div className="pl-1">
          <div>
            {'1. '}
            <Text strong>{t('Pre-auth token check')}</Text>
            {' \u2014 '}
            {t('If the embedding page provides a token, it is validated against configured providers.')}
          </div>
          <Text type="secondary" className="pl-4 d-block">
            {'\u2192 '}{t('Valid token')} {'\u2192 '}<Text type="success">{t('Success exit')}</Text>
          </Text>
          <div className="mt-1">
            {'2. '}
            <Text strong>{t('OIDC sign-in prompt')}</Text>
            {' \u2014 '}
            {t('If no pre-auth token matched, a sign-in button is shown in the webchat.')}
          </div>
          <Text type="secondary" className="pl-4 d-block">
            {'\u2192 '}{t('User signs in')} {'\u2192 '}<Text type="success">{t('Success exit')}</Text>
            {'  |  '}
            {t('Fails / dismissed')} {'\u2192 '}<Text type="danger">{t('Error exit')}</Text>
          </Text>
        </div>
        <Text italic type="secondary">
          {t('When "Check only" is enabled, step 2 is skipped. Unauthenticated users go directly to the Error exit without seeing a sign-in prompt.')}
        </Text>
      </Space>

      {preAuthProviders.length > 0 && (
        <Space className="mb-4" size={4}>
          <Text type="secondary">{t('Pre-auth providers:')}</Text>
          {preAuthProviders.map((p, i) => (
            <Tag key={i}>{p.name}</Tag>
          ))}
        </Space>
      )}

      <Form.Item
        name={['data', 'requireOidcSignIn']}
        initialValue={true}
        getValueFromEvent={(e) => !e.target.checked}
        getValueProps={(value) => ({ checked: value === false })}
      >
        <Checkbox disabled={!hasOidcConfig}>
          {t('Check only if user is authenticated')}
        </Checkbox>
      </Form.Item>

      {hasOidcConfig && (
        <Fragment>
          <Divider orientation="left" className="mt-0">
            <Text type="secondary">{t('OIDC Settings')}</Text>
          </Divider>

          <LabelWithOriginalText label={t('Message Text')} originalText={form.getFieldValue(['data', 'label', primaryBotLanguage])} />
          {
            botLanguages.map(lang => (
              <Form.Item
                key={`authenticate-user-message-${lang}`}
                label=""
                name={['data', 'label', lang]}
                hidden={currentBotLanguage !== lang}
                rules={[{ required: !checkOnly && lang === primaryBotLanguage && isEditingPrimaryLanguage, message: t('Message text cannot be blank.') }]}
              >
                <Input
                  size="large"
                  disabled={checkOnly}
                  placeholder={t('Enter the message shown to the user')}
                />
              </Form.Item>
            ))
          }

          <LabelWithOriginalText label={t('Button Label')} originalText={form.getFieldValue(['data', 'buttonLabel', primaryBotLanguage])} />
          {
            botLanguages.map(lang => (
              <Form.Item
                key={`authenticate-user-button-${lang}`}
                label=""
                name={['data', 'buttonLabel', lang]}
                hidden={currentBotLanguage !== lang}
                rules={[{ required: !checkOnly && lang === primaryBotLanguage && isEditingPrimaryLanguage, message: t('Button label cannot be blank.') }]}
              >
                <Input
                  size="large"
                  disabled={checkOnly}
                  placeholder={t('Enter the button text')}
                />
              </Form.Item>
            ))
          }
        </Fragment>
      )}
    </Fragment>
  );
};

AuthenticateUserNodeForm.propTypes = {
  form: PropTypes.object.isRequired
};

export default AuthenticateUserNodeForm;
